import { Divider, Stack } from '@dtx-company/design-system/src'
import {
  HelpOutline,
  LogoutOutlined,
  OpenInNewSharp,
  SettingsOutlined,
  WebAssetSharp
} from '@mui/icons-material'
import { INTERCOM_NAV_HELP_LINK } from '@app/common/src/constants/Nav'
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { MarketingPageRoutes, Routes } from '@dtx-company/true-common/src/constants/routes'
import { SettingsMenuOption } from '../types'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { useLogout } from '@dtx-company/inter-app/src/hooks/auth/useLogout'
import { useRouter } from 'next/router'
import PresentOutline from '@dtx-company/shared-components/src/foundation/Icons/NavV3/PresentOutline'
import React from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'
import useTranslation from 'next-translate/useTranslation'

interface SettingsListProps {
  closeAllMenus: () => void
  showReferAFriendOption: boolean
  isDesktop?: boolean
}

export const SettingsList: React.FC<SettingsListProps> = ({
  closeAllMenus,
  showReferAFriendOption,
  isDesktop
}) => {
  const { push } = useRouter()
  const logout = useLogout()
  const { t } = useTranslation('nav')

  return (
    <List
      sx={theme => ({
        '.MuiListItemIcon-root': { minWidth: 'unset', marginRight: theme.spacing(4) }
      })}
    >
      <SettingsListOption
        isDesktop={isDesktop}
        title={t('ProfileMenu.Settings')}
        icon={<SettingsOutlined />}
        onClick={() => {
          closeAllMenus()
          push(Routes.ACCOUNT)
        }}
        testId="settings-settings-list-option"
        heapId="Settings"
      />
      <SettingsListOption
        isDesktop={isDesktop}
        title={t('ProfileMenu.GetHelp')}
        icon={<HelpOutline />}
        onClick={() => {
          window.open(INTERCOM_NAV_HELP_LINK, '_blank')
        }}
        testId="help-settings-list-option"
        heapId="Get Help"
      />
      {showReferAFriendOption && (
        <SettingsListOption
          isDesktop={isDesktop}
          title={t('ProfileMenu.ReferAFriend')}
          icon={<PresentOutline />}
          onClick={() => {
            closeAllMenus()
            push({
              pathname: Routes.ACCOUNT,
              query: 'referrals'
            })
          }}
          testId="referral-settings-list-option"
          heapId="Refer a Friend"
        />
      )}
      <SettingsListOption
        isDesktop={isDesktop}
        title={t('ProfileMenu.FlowcodeHomepage')}
        icon={<WebAssetSharp />}
        onClick={() => {
          window.open(MarketingPageRoutes.INDEX, '_blank')
        }}
        newTab
        testId="homepage-settings-list-option"
        heapId="Flowcode Homepage"
      />
      <SettingsListOption
        isDesktop={isDesktop}
        title={t('ProfileMenu.SignOut')}
        icon={<LogoutOutlined />}
        color="error.main"
        onClick={async () => {
          closeAllMenus()
          await logout()
        }}
        testId="signout-settings-list-option"
        heapId="Sign Out"
      />
      <Spacer mb="8px" />
      <Divider />
    </List>
  )
}

const SettingsListOption: React.FC<SettingsMenuOption> = ({
  title,
  onClick,
  icon,
  color,
  isDesktop,
  newTab,
  testId,
  heapId
}) => {
  return (
    <ListItem disablePadding>
      <ListItemButton
        data-testid={testId}
        onClick={() => {
          isDesktop
            ? events.Platform_Desktop_TopNav_ProfileMenu_Clicked_MenuItem(heapId)
            : events.Platform_MobileMoreOptionsSheet_ProfileMenu_Clicked_MenuItem(heapId)
          onClick?.()
        }}
      >
        <ListItemIcon sx={{ color: color ?? 'onSurface.secondary' }}>{icon}</ListItemIcon>
        <ListItemText primary={title} sx={{ color }} />
        {newTab && (
          <Stack sx={{ '.MuiListItemIcon-root': { mr: 0 }, alignItems: 'center' }}>
            <ListItemIcon sx={{ color: color ?? 'onSurface.secondary' }}>
              <OpenInNewSharp />
            </ListItemIcon>
          </Stack>
        )}
      </ListItemButton>
    </ListItem>
  )
}
